import {
  Box,
  Button,
  DialogProps,
  IconButton,
  OutlinedInput,
  ScopedCssBaseline,
  ThemeProvider,
  Typography,
  useTheme
} from '@mui/material';
import { capitalMuiDarkTheme, capitalMuiTheme } from '@opengov/capital-mui-theme';
import { Close as CloseIcon, Empty as EmptyIllustration, Magnify as SearchIcon } from '@opengov/react-capital-assets';
import React, { useEffect, useRef } from 'react';

import { DATA_TEST, getDynamicDataTestId } from '../NavBar/enums';

import { DialogContainer } from './DialogContainer';
import { ErrorResults } from './ErrorResults';
import { LoadingSkeleton } from './LoadingSkeleton';
import { PreviewPanelPlaceholder } from './PreviewPanelPlaceholder';
import { ISearchContext, SearchContext, SearchContextProvider, TType } from './SearchContext';
import { FilterLabel, FilterOptions, SearchFilters } from './SearchFilters';

import { styles } from './common-styles';

// This is a workaround caused by this repo failing to connect module augmentations within sibling libraries.
// This should not be a permanent solution as it duplicates type declarations rather than them having a single
// source of truth.
declare module '@mui/material/InputBase' {
  // tslint:disable-next-line:interface-name
  interface InputBasePropsSizeOverrides {
    large: true;
  }
}
declare module '@mui/material/Chip' {
  // tslint:disable-next-line:interface-name
  interface ChipPropsSizeOverrides {
    large: true;
  }
  // tslint:disable-next-line:interface-name
  interface ChipPropsVariantOverrides {
    minimal: true;
    strong: true;
  }
}
// End of workaround

const searchTableStyles = {
  searchResultsContainer: {
    flex: 1,
    // Does not apply bottom padding, searchItemsContainer.paddingBottom will handle that to accommodate scroll
    paddingTop: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1 / 2
  },
  searchItemsContainer: {
    // Give the scroll a little bit of space to breathe, matches searchResultsContainer.paddingTop
    paddingBottom: 2,
    paddingRight: 2,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto'
  },
  tableLabel: {
    paddingLeft: 1,
    paddingBottom: 1
  },
  searchResultRow: {
    userSelect: 'none',
    cursor: 'pointer',
    borderBottom: 1,
    borderColor: 'divider',
    '&:hover': {
      bgcolor: 'action.hover'
    },
    ':focus-visible, &.Mui-focusVisible': {
      bgcolor: 'action.hover',
      borderRadius: 1,
      outlineOffset: -2
    }
  },
  highlighted: {
    bgcolor: 'action.hover'
  }
};

function SearchItemsTableContainer({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <Box sx={searchTableStyles.searchResultsContainer}>
      <Typography variant="h6" component="span" sx={searchTableStyles.tableLabel}>
        {title}
      </Typography>
      <Box tabIndex={-1} sx={searchTableStyles.searchItemsContainer}>
        {children}
      </Box>
    </Box>
  );
}

const SearchPanelTypeContext = React.createContext<'suggestions' | 'results' | null>(null);

type IRowRender<T> = ({ index, rowData }: { index: number; rowData: T }) => React.ReactNode;

export function SearchSuggestions<TResult, TSuggestion>({ children }: { children: React.ReactNode; title?: string }) {
  const { results, isLoading, searchInput, error } = React.useContext(
    SearchContext as React.Context<ISearchContext<TResult, TSuggestion>>
  );
  const showSuggestions = (!results && !isLoading) || searchInput.length === 0;
  if (!showSuggestions || error) return null;
  return (
    <SearchPanelTypeContext.Provider value="suggestions">
      <Box sx={styles.searchWithPreview}>{children}</Box>
    </SearchPanelTypeContext.Provider>
  );
}

export const SearchTableContext = React.createContext<string>('');

export function SearchTable<T extends TType>({
  children,
  title = 'Results',
  gridTemplateColumns = '1fr 1fr 1fr'
}: {
  children: IRowRender<T>;
  title?: string;
  gridTemplateColumns?: string;
}) {
  const { suggestions, results, focusIndex, isLoading, setFocusIndex, onSelectSearchResult, onSelectSearchSuggestion } =
    React.useContext(SearchContext as React.Context<ISearchContext<T, T>>);
  const typeOfTable = React.useContext(SearchPanelTypeContext);
  if (!typeOfTable) throw new Error('SearchTable must be used within a SearchSuggestions or SearchResults');
  const tableItems = typeOfTable === 'suggestions' ? suggestions : results;
  const showLoading = typeOfTable === 'results' && isLoading;

  // itemRefs are required to enable scrolling to focused item, needed for kb nav
  const itemRefs = useRef<React.RefObject<HTMLDivElement[]> | []>([]);
  useEffect(() => {
    if (focusIndex !== null && focusIndex > -1 && itemRefs.current[focusIndex] && !showLoading) {
      itemRefs.current[focusIndex].current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }, [focusIndex]);

  const onClickHandler = typeOfTable === 'suggestions' ? onSelectSearchSuggestion : onSelectSearchResult;

  return (
    <SearchTableContext.Provider value={gridTemplateColumns}>
      <SearchItemsTableContainer title={title}>
        {showLoading ? (
          <LoadingSkeleton />
        ) : (
          tableItems?.map((rowData, index) => {
            if (!itemRefs.current[index]) {
              itemRefs.current[index] = React.createRef();
            }
            return (
              <Box
                id={`search-row-${index}`}
                ref={itemRefs.current[index]}
                sx={[
                  focusIndex === index
                    ? { ...searchTableStyles.searchResultRow, ...searchTableStyles.highlighted }
                    : searchTableStyles.searchResultRow,
                  (theme) => ({
                    ':focus-visible, &.Mui-focusVisible': {
                      outline: `2px solid ${theme.palette.primary.main}`
                    }
                  })
                ]}
                onMouseEnter={() => setFocusIndex(index)}
                onClick={() => onClickHandler?.(rowData)}
                onFocus={() => setFocusIndex(index)}
                key={rowData.id}
                tabIndex={0}
                role="button"
                data-test={getDynamicDataTestId(DATA_TEST.global_search_row, `${index}`)}
              >
                {children({ index, rowData })}
              </Box>
            );
          })
        )}
      </SearchItemsTableContainer>
    </SearchTableContext.Provider>
  );
}

export const NoResults = ({ children }: { children?: React.ReactNode }) => {
  const emptyContent = (
    <>
      <EmptyIllustration role="presentation" height="160px" />
      <Typography variant="bodyLarge" mt={3}>
        No Results Found
      </Typography>
      <Typography variant="bodySmall" color="secondary.main" mb={2}>
        Please try entering a different search term.
      </Typography>
    </>
  );

  return <Box sx={styles.searchEmpty}>{children ? children : emptyContent}</Box>;
};

export function SearchResults<TResult extends TType | null>({
  children,
  noResultsComponent
}: Readonly<{
  children: React.ReactNode;
  noResultsComponent?: React.ReactNode;
}>): React.ReactElement | null {
  const context = React.useContext(SearchContext as React.Context<ISearchContext<TResult, unknown>>);
  if (!context) throw new Error('SearchResults must be used within a SearchContextProvider');
  const { results, isLoading, searchInput, error } = context;
  if (error) return typeof error === 'string' ? <ErrorResults description={error} /> : (error as React.ReactElement);
  const showResults = (!!results || isLoading) && searchInput.length > 0;
  if (!showResults) return null;
  if (results && results.length === 0 && !isLoading) return <NoResults>{noResultsComponent}</NoResults>;
  return (
    <SearchPanelTypeContext.Provider value="results">
      <Box sx={styles.searchWithPreview}>{children}</Box>
    </SearchPanelTypeContext.Provider>
  );
}

const previewContainerStyle = {
  width: 320,
  padding: 2,
  borderLeft: 1,
  borderColor: 'divider'
};

export function FocusedPreview<T extends TType>({ children }: { children: IRowRender<T> }) {
  const { focusedItem, focusIndex, isLoading, layoutMode } = React.useContext(
    SearchContext as React.Context<ISearchContext<T, T>>
  );
  // This is just to confirm consumers put this table in the correct spot in JSX
  const typeOfTable = React.useContext(SearchPanelTypeContext);
  if (!typeOfTable) throw new Error('FocusedPreview must be used within a SearchSuggestions or SearchResults');

  if (layoutMode === 'mobile') return null;

  if (focusIndex === null || focusIndex < 0 || !focusedItem || (typeOfTable === 'results' && isLoading))
    return <PreviewPanelPlaceholder />;
  return <Box sx={previewContainerStyle}>{children({ rowData: focusedItem, index: focusIndex })}</Box>;
}

const searchInputStyles = {
  searchInput: {
    borderRadius: '100px !important',
    margin: 0,
    // tslint:disable-next-line:max-line-length
    '&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
      {
        borderRadius: '100px !important'
      },
    '& svg': {
      transform: 'scale(1.5)',
      fill: 'text.secondary'
    }
  },
  clearButton: {
    marginRight: '16px !important'
  }
};

interface ISearchDialogContainer {
  children: React.ReactNode;
  maxWidth: DialogProps['maxWidth'];
  open?: boolean;
  activeSearchFilter?: string;
  searchFilterOptions?: FilterOptions[];
  placeholder?: string;
  isLoading?: boolean;
  filterLabel?: FilterLabel;
  onClose?(): void;
  onSearchInputChange(searchInput: string): void;
  onActiveSearchFilterChange?(activeSearchFilter: string): void;
}
const SearchDialogContainer = ({
  children,
  open = false,
  onClose,
  onSearchInputChange,
  activeSearchFilter = '',
  onActiveSearchFilterChange = () => {},
  searchFilterOptions,
  placeholder = '',
  maxWidth,
  filterLabel
}: ISearchDialogContainer) => {
  const context = React.useContext(SearchContext);
  if (!context) throw new Error('SearchResults must be used within a SearchContextProvider');
  const { searchInput, focusIndex, setFocusIndex } = context;

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!open) {
      setFocusIndex(-1);
    }
  }, [open]);

  return (
    <DialogContainer open={open} onClose={onClose} maxWidth={maxWidth}>
      <Box display="flex" alignItems="center" gap={1}>
        <OutlinedInput
          id="search-input"
          inputRef={searchInputRef}
          sx={searchInputStyles.searchInput}
          fullWidth={true}
          size="large"
          data-test={DATA_TEST.global_search_input}
          placeholder={placeholder}
          inputProps={{
            'aria-label': 'Search',
            'aria-activedescendant': focusIndex !== null && focusIndex > -1 ? `search-row-${focusIndex}` : undefined,
            sx: { border: 'none !important' }
          }}
          startAdornment={<SearchIcon color="secondary" fontSize="large" sx={{ ml: 0.5 }} />}
          endAdornment={
            searchInput.length > 0 && (
              <Button
                color="secondary"
                variant="text"
                data-test={DATA_TEST.global_search_clear}
                sx={searchInputStyles.clearButton}
                onClick={() => {
                  onSearchInputChange('');
                  searchInputRef.current?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSearchInputChange('');
                    searchInputRef.current?.focus();
                  }
                }}
              >
                Clear
              </Button>
            )
          }
          value={searchInput}
          onFocus={() => {
            // Ensures that voiceover announces the search input value when focus moves back to it
            setFocusIndex(-1);
          }}
          onChange={(e) => onSearchInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              // Prevent the default action to keep the cursor position
              e.preventDefault();
            }
          }}
        />
        <IconButton
          onClick={onClose}
          aria-label="Close search"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onClose?.();
            }
          }}
          data-test={DATA_TEST.global_search_close}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {searchFilterOptions && searchFilterOptions.length > 0 && (
        <SearchFilters
          activeFilter={activeSearchFilter}
          onFilterChange={onActiveSearchFilterChange}
          filterOptions={searchFilterOptions}
          filterLabel={filterLabel}
        />
      )}
      {children}
    </DialogContainer>
  );
};

export type ISearchDialog<TResult extends TType | null, TSuggestion extends TType | null> = {
  children: React.ReactNode;
  open?: boolean;
  searchInput?: string;
  searchResults?: TResult[];
  searchSuggestions?: TSuggestion[];
  activeSearchFilter?: string;
  searchFilterOptions?: FilterOptions[];
  placeholder?: string;
  isLoading?: boolean;
  error?: string | React.ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  filterLabel?: FilterLabel;
  onClose?(): void;
  onSearchInputChange(searchInput: string): void;
  onSelectSearchResult(searchResult: TResult): void;
  onSelectSearchSuggestion?(searchSuggestion: TSuggestion): void;
  onActiveSearchFilterChange?(activeSearchFilter: string): void;
};

export function SearchDialog<TResult extends TType | null, TSuggestion extends TType | null>(
  props: ISearchDialog<TResult, TSuggestion>
): React.ReactElement {
  // Consumers can optionally use this component uncontrolled, meaning it manages it's own
  // state internally. These are the props that are used to control the component.
  const outerTheme = useTheme();
  const [localSearchInput, setLocalSearchInput] = React.useState('');
  const [localOpen, setLocalOpen] = React.useState(false);
  const searchInput =
    props.searchInput === null || props.searchInput === undefined ? localSearchInput : props.searchInput;
  const isOpen = props.open || localOpen;
  return (
    <ThemeProvider theme={outerTheme.palette.mode === 'dark' ? capitalMuiDarkTheme : capitalMuiTheme}>
      <ScopedCssBaseline>
        <SearchContextProvider
          searchInput={searchInput}
          results={props.searchResults || []}
          suggestions={props.searchSuggestions || []}
          noResults={!!props.searchResults && props.searchResults.length === 0}
          isLoading={props.isLoading || false}
          error={props.error}
          onSelectSearchResult={props.onSelectSearchResult}
          onSelectSearchSuggestion={props.onSelectSearchSuggestion}
          isOpen={isOpen}
        >
          <SearchDialogContainer
            {...props}
            open={isOpen}
            maxWidth={props.maxWidth || 'md'}
            onSearchInputChange={(input: string) => {
              setLocalSearchInput(input);
              props.onSearchInputChange(input);
            }}
            onClose={() => {
              setLocalSearchInput('');
              if (props.onClose) {
                props.onClose();
              } else {
                if (props.open === undefined) {
                  setLocalOpen(false);
                }
              }
            }}
          />
        </SearchContextProvider>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
